import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import format from 'date-fns/format';

import Spinner from '../../../../../../components/global/Spinner';
import Button from '../../../../../../components/global/Button';
import PlayButtonIcon from '../../../../../../components/icons/PlayButtonIcon';
import PauseButtonIcon from '../../../../../../components/icons/PauseButtonIcon';
import Angle from '../../../../../../components/global/Angle';
import { getAsset } from '../../../../../../util/assets';

const propTypes = PropTypes.shape({
  loadingDevices: PropTypes.bool,
  cameraStream: PropTypes.any.isRequired,
  recording: PropTypes.bool,
  onRecordingStart: PropTypes.func.isRequired,
  onRecordingStop: PropTypes.func.isRequired,
  onContinue: PropTypes.func,
  recordedVideoUrl: PropTypes.string,
  remainingTime: PropTypes.number,
  onBack: PropTypes.func,
  maxRecordingTime: PropTypes.number,
  onDeviceChange: PropTypes.func,
  onRecordingReset: PropTypes.func,
});

const FullScreenVideoRecorder = ({
  loadingDevices,
  onRecordingStart,
  onRecordingStop,
  recording,
  cameraStream,
  recordedVideoUrl,
  onContinue,
  onBack,
  remainingTime,
  maxRecordingTime,
  onDeviceChange,
  onRecordingReset,
}) => {
  const videoRef = useRef(null);
  const recordedVideoRef = useRef(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  useEffect(() => {
    document.body.classList.add('full-screen-video-recorder-container');

    return () => {
      document.body.classList.remove('full-screen-video-recorder-container');
    };
  }, []);

  useEffect(() => {
    if (
      videoRef.current &&
      // Ensures we don't reset the video on the same stream.
      videoRef.current.srcObject?.id !== cameraStream?.current?.id
    ) {
      videoRef.current.srcObject = cameraStream?.current;
    }
  });

  return (
    <div className="full-screen-video-recorder">
      {loadingDevices && (
        <div className="full-screen-video-recorder-loading">
          <Spinner className="m-auto" />
        </div>
      )}
      {!recording && recordedVideoUrl && (
        <video ref={recordedVideoRef} playsInline src={recordedVideoUrl} />
      )}
      <video
        playsInline
        autoPlay
        ref={videoRef}
        muted
        className={recordedVideoUrl && !recording ? 'd-none' : ''}
      />
      {!loadingDevices && (
        <div className="full-screen-video-recorder-top-actions">
          <div className="left-action">
            {onBack && (
              <Button
                btnStyle="reset"
                className={!recording ? '' : 'd-none'}
                onClick={onBack}
              >
                <Angle direction="left" className="mr-1" /> Back
              </Button>
            )}
          </div>

          {recording && (
            <div className="center-action">
              <span
                className={recording ? 'text-lg text-extra-bold' : 'd-none'}
              >
                {format(new Date(maxRecordingTime - remainingTime), 'mm:ss')}/
                {format(new Date(maxRecordingTime), 'mm:ss')}
              </span>
            </div>
          )}
          <div className="right-action" />
        </div>
      )}
      {!loadingDevices && (
        <>
          {(() => {
            if (!recording && recordedVideoUrl) {
              return (
                <div className="full-screen-video-recorder-actions">
                  <div className="left-action">
                    <Button
                      btnSize="sm"
                      pill
                      btnStyle="white"
                      onClick={onRecordingReset}
                      className="mr-1"
                    >
                      Retake
                    </Button>
                  </div>
                  <div className="center-action">
                    <Button
                      btnStyle="reset"
                      btnSize="sm"
                      className="d-flex"
                      onClick={() => {
                        if (isVideoPlaying) {
                          recordedVideoRef?.current?.pause();
                          setIsVideoPlaying(false);
                        } else {
                          recordedVideoRef?.current?.play();
                          setIsVideoPlaying(true);
                        }
                      }}
                    >
                      {isVideoPlaying ? (
                        <PauseButtonIcon size={40} color="#fff" />
                      ) : (
                        <PlayButtonIcon size={40} color="#fff" />
                      )}
                    </Button>
                  </div>
                  <div className="right-action">
                    <Button
                      btnSize="sm"
                      pill
                      btnStyle="primary"
                      onClick={onContinue}
                      className="ml-1"
                    >
                      Upload
                    </Button>
                  </div>
                </div>
              );
            }

            if (recording) {
              return (
                <div className="full-screen-video-recorder-actions">
                  <div className="center-action">
                    <Button
                      btnStyle="reset"
                      onClick={onRecordingStop}
                      className="full-screen-video-recorder-record-btn full-screen-video-recorder-record-btn-stop"
                    />
                  </div>
                </div>
              );
            }

            return (
              <div className="full-screen-video-recorder-actions">
                <div className="left-action" />
                <div className="center-action">
                  <Button
                    btnStyle="reset"
                    onClick={onRecordingStart}
                    className="full-screen-video-recorder-record-btn"
                  />
                </div>
                <div className="right-action">
                  {onDeviceChange && (
                    <Button
                      btnStyle="reset"
                      className="selfie-change-camera-action"
                      onClick={onDeviceChange}
                    >
                      <img
                        src={getAsset('/icon-restore.svg')}
                        alt="swap camera"
                      />
                    </Button>
                  )}
                </div>
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
};

FullScreenVideoRecorder.propTypes = propTypes;

export default FullScreenVideoRecorder;
